import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  LinearProgress,
  Avatar,
  Badge,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tooltip,
  Button,
  Grid,
  useTheme,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  Stop,
  Refresh,
  ExpandMore,
  ExpandLess,
  Error,
  CheckCircle,
  Schedule,
  Memory,
  Speed,
  DataObject,
  BugReport,
  Timeline,
  Analytics,
  Storage,
  CloudDownload,
  Visibility,
  Clear,
  FilterList,
} from "@mui/icons-material";

const ExecutionsView = ({ nodes = [], edges = [], executionState = {} }) => {
  const theme = useTheme();
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set(["recent"]));
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewMode, setViewMode] = useState("timeline"); // timeline, analytics, logs
  const refreshInterval = useRef(null);

  // Real execution history data from SignalR events and executionState
  const executionHistory = useMemo(() => {
    const executions = [];

    // Get real execution data from nodes with execution state
    nodes.forEach((node) => {
      const nodeExecution = executionState[node.id];
      if (nodeExecution && nodeExecution.status !== "idle") {
        // Only include nodes that have actually been executed
        const execution = {
          id: `exec-${node.id}-${nodeExecution.startTime || Date.now()}`,
          nodeId: node.id,
          nodeName: node.data?.label || node.type,
          nodeType: node.type,
          status: nodeExecution.status,
          startTime: nodeExecution.startTime,
          endTime: nodeExecution.endTime,
          duration: nodeExecution.duration,
          recordsProcessed: nodeExecution.recordCount, // Use recordCount from SignalR
          errorMessage: nodeExecution.error,
          output: nodeExecution.sampleData,
          // Real execution metrics from backend if available
          metrics: {
            cpuUsage: nodeExecution.cpuUsage,
            memoryPeak: nodeExecution.memoryUsage,
            ioOperations: nodeExecution.ioOperations,
            networkCalls: nodeExecution.networkCalls,
          },
        };

        // Debug logging for execution data
        console.log("🔍 ExecutionsView - Node execution data:", {
          nodeId: node.id,
          status: execution.status,
          errorMessage: execution.errorMessage,
          shouldShowError:
            execution.errorMessage &&
            (execution.status === "error" || execution.status === "failed"),
        });

        // Only add executions with actual start times or status changes (real executions)
        if (execution.startTime || execution.status !== "idle") {
          executions.push(execution);
        }
      }
    });

    return executions.sort((a, b) => (b.startTime || 0) - (a.startTime || 0));
  }, [nodes, executionState]);

  const filteredExecutions = useMemo(() => {
    if (filterStatus === "all") return executionHistory;
    return executionHistory.filter((exec) => exec.status === filterStatus);
  }, [executionHistory, filterStatus]);

  const executionStats = useMemo(() => {
    const total = executionHistory.length;
    const completed = executionHistory.filter((e) => e.status === "completed").length;
    const errors = executionHistory.filter((e) => e.status === "error").length;
    const running = executionHistory.filter((e) => e.status === "running").length;

    const avgDuration =
      executionHistory.length > 0
        ? Math.round(executionHistory.reduce((sum, e) => sum + (e.duration || 0), 0) / total)
        : 0;

    const totalRecords = executionHistory.reduce((sum, e) => sum + (e.recordsProcessed || 0), 0);

    return {
      total,
      completed,
      errors,
      running,
      successRate: total > 0 ? ((completed / total) * 100).toFixed(1) : 0,
      avgDuration,
      totalRecords,
    };
  }, [executionHistory]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return theme.palette.success.main;
      case "error":
        return theme.palette.error.main;
      case "running":
        return theme.palette.warning.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle fontSize="small" />;
      case "error":
        return <Error fontSize="small" />;
      case "running":
        return <Schedule fontSize="small" />;
      default:
        return <Schedule fontSize="small" />;
    }
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  const formatMemory = (bytes) => {
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) return `${mb.toFixed(1)}MB`;
    return `${(mb / 1024).toFixed(1)}GB`;
  };

  const formatSafeDate = (timestamp) => {
    if (!timestamp) return null;
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return null;
      return date.toISOString();
    } catch {
      return null;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "Invalid date";

      const now = new Date();
      const diffMs = now - date;

      if (diffMs < 60000) return "Just now";
      if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
      if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
      return date.toLocaleDateString();
    } catch {
      return "Invalid date";
    }
  };

  useEffect(() => {
    if (autoRefresh) {
      refreshInterval.current = setInterval(() => {
        // Auto-refresh is handled by SignalR events in real-time
        // No need to manually refresh mock data
        console.log("Execution data is updated in real-time via SignalR events");
      }, 5000);
    } else {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
        refreshInterval.current = null;
      }
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [autoRefresh]);

  if (nodes.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 40px)",
          backgroundColor: "background.default",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <Timeline
            sx={{
              fontSize: 80,
              mb: 3,
              opacity: 0.4,
              color: "text.secondary",
            }}
          />
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              color: "text.primary",
              fontWeight: 500,
            }}
          >
            No Executions Yet
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: "300px",
            }}
          >
            Execute workflows from the "AGENTIC Editor" tab to see real execution logs and analytics
            here
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 40px)",
        backgroundColor: "background.default",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Header with controls */}
      <Box
        sx={{
          p: 3,
          pb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: "bold" }}
          >
            Execution Monitor
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "11px" }}>
              {executionStats.total} executions
            </Typography>
            <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "success.main" }} />
            <Typography variant="caption" sx={{ color: "success.main", fontSize: "11px" }}>
              {executionStats.successRate}% success
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {/* View mode toggle */}
          <Button
            size="small"
            variant={viewMode === "timeline" ? "contained" : "outlined"}
            onClick={() => setViewMode("timeline")}
            startIcon={<Timeline fontSize="small" />}
          >
            Timeline
          </Button>
          <Button
            size="small"
            variant={viewMode === "analytics" ? "contained" : "outlined"}
            onClick={() => setViewMode("analytics")}
            startIcon={<Analytics fontSize="small" />}
          >
            Analytics
          </Button>

          {/* Status filter */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterList fontSize="small" />}
            onClick={(e) => {
              // In real implementation, show dropdown menu
              const next =
                filterStatus === "all"
                  ? "completed"
                  : filterStatus === "completed"
                    ? "error"
                    : filterStatus === "error"
                      ? "running"
                      : "all";
              setFilterStatus(next);
            }}
          >
            {filterStatus === "all" ? "All" : filterStatus}
          </Button>

          {/* Auto refresh toggle */}
          <IconButton
            size="small"
            onClick={() => setAutoRefresh(!autoRefresh)}
            sx={{
              color: autoRefresh ? "success.main" : "text.secondary",
              bgcolor: autoRefresh ? "success.light" : "action.hover",
              "&:hover": {
                bgcolor: autoRefresh ? "success.main" : "action.selected",
              },
            }}
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Main content area */}
      <Box sx={{ flex: 1, overflow: "hidden", display: "flex" }}>
        {/* Left panel - Execution list or analytics */}
        <Box
          sx={{
            width: selectedExecution ? "40%" : "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {viewMode === "analytics" ? (
            // Analytics view
            <Box sx={{ p: 3, overflow: "auto" }}>
              <Grid container spacing={3}>
                {/* Stats cards */}
                <Grid item xs={12} md={6} lg={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h4" sx={{ color: "primary.main", fontWeight: 600 }}>
                      {executionStats.total}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Total Executions
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h4" sx={{ color: "success.main", fontWeight: 600 }}>
                      {executionStats.successRate}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Success Rate
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h4" sx={{ color: "warning.main", fontWeight: 600 }}>
                      {formatDuration(executionStats.avgDuration)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Avg Duration
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h4" sx={{ color: "info.main", fontWeight: 600 }}>
                      {executionStats.totalRecords.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Records Processed
                    </Typography>
                  </Paper>
                </Grid>

                {/* Performance chart placeholder */}
                <Grid item xs={12} lg={8}>
                  <Paper sx={{ p: 3, height: 300 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Execution Performance Over Time
                    </Typography>
                    <Box
                      sx={{
                        height: 200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "background.default",
                        borderRadius: 1,
                        border: "1px dashed",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        📊 Performance Chart (Chart.js integration)
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                {/* Node execution breakdown */}
                <Grid item xs={12} lg={4}>
                  <Paper sx={{ p: 3, height: 300 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Node Execution Breakdown
                    </Typography>
                    <Box sx={{ overflow: "auto", height: 200 }}>
                      {nodes.map((node) => {
                        const nodeExecutions = executionHistory.filter((e) => e.nodeId === node.id);
                        const successCount = nodeExecutions.filter(
                          (e) => e.status === "completed"
                        ).length;
                        const successRate =
                          nodeExecutions.length > 0
                            ? (successCount / nodeExecutions.length) * 100
                            : 0;

                        return (
                          <Box key={node.id} sx={{ mb: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography variant="caption" sx={{ fontSize: "11px" }}>
                                {node.data?.label || node.type}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "11px", color: "text.secondary" }}
                              >
                                {successRate.toFixed(0)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={successRate}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: "action.hover",
                                "& .MuiLinearProgress-bar": {
                                  bgcolor:
                                    successRate > 80
                                      ? "success.main"
                                      : successRate > 50
                                        ? "warning.main"
                                        : "error.main",
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          ) : (
            // Timeline view
            <Box sx={{ overflow: "auto", flex: 1 }}>
              {/* Recent executions */}
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", cursor: "pointer", mb: 1 }}
                  onClick={() => toggleSection("recent")}
                >
                  <IconButton size="small">
                    {expandedSections.has("recent") ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Recent Executions ({filteredExecutions.slice(0, 20).length})
                  </Typography>
                  {executionStats.running > 0 && (
                    <Badge badgeContent={executionStats.running} color="warning" sx={{ ml: 1 }} />
                  )}
                </Box>

                <Collapse in={expandedSections.has("recent")}>
                  <List sx={{ pt: 0 }}>
                    {filteredExecutions.slice(0, 20).map((execution) => (
                      <ListItem
                        key={execution.id}
                        sx={{
                          cursor: "pointer",
                          borderRadius: 1,
                          mb: 0.5,
                          bgcolor:
                            selectedExecution?.id === execution.id
                              ? "action.selected"
                              : "transparent",
                          "&:hover": { bgcolor: "action.hover" },
                          border: "1px solid",
                          borderColor:
                            selectedExecution?.id === execution.id ? "primary.main" : "transparent",
                        }}
                        onClick={() => setSelectedExecution(execution)}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Box sx={{ color: getStatusColor(execution.status) }}>
                            {getStatusIcon(execution.status)}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {execution.nodeName}
                              </Typography>
                              <Chip
                                label={execution.nodeType}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "10px",
                                  bgcolor: "action.hover",
                                  color: "text.secondary",
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "10px", color: "text.secondary" }}
                              >
                                {formatTimestamp(execution.startTime)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "10px", fontFamily: "monospace" }}
                              >
                                {formatDuration(execution.duration)}
                              </Typography>
                              {execution.recordsProcessed && (
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: "10px", fontFamily: "monospace" }}
                                >
                                  {execution.recordsProcessed.toLocaleString()} records
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        {execution.status === "running" && (
                          <LinearProgress
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: 2,
                            }}
                          />
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            </Box>
          )}
        </Box>

        {/* Right panel - Execution details */}
        {selectedExecution && (
          <Box
            sx={{
              width: "60%",
              borderLeft: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Execution details header */}
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ color: getStatusColor(selectedExecution.status) }}>
                    {getStatusIcon(selectedExecution.status)}
                  </Box>
                  {selectedExecution.nodeName}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Execution ID: {selectedExecution.id}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelectedExecution(null)}>
                <Clear fontSize="small" />
              </IconButton>
            </Box>

            {/* Execution details content */}
            <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
              <Grid container spacing={2}>
                {/* Status and timing */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Schedule fontSize="small" />
                      Execution Timeline
                    </Typography>
                    <Box sx={{ space: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="caption">Started:</Typography>
                        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                          {selectedExecution.startTime
                            ? formatSafeDate(selectedExecution.startTime)
                              ? new Date(selectedExecution.startTime).toLocaleString()
                              : "Invalid date"
                            : "N/A"}
                        </Typography>
                      </Box>
                      {selectedExecution.endTime && (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="caption">Ended:</Typography>
                          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                            {formatSafeDate(selectedExecution.endTime)
                              ? new Date(selectedExecution.endTime).toLocaleString()
                              : "Invalid date"}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="caption">Duration:</Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "monospace", fontWeight: 600 }}
                        >
                          {selectedExecution.duration
                            ? formatDuration(selectedExecution.duration)
                            : "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Performance metrics */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Speed fontSize="small" />
                      Performance Metrics
                    </Typography>
                    <Box sx={{ space: 1 }}>
                      {selectedExecution.metrics.cpuUsage !== undefined ? (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="caption">CPU Usage:</Typography>
                          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                            {selectedExecution.metrics.cpuUsage}%
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "text.disabled" }}>
                            CPU Usage:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: "monospace", color: "text.disabled" }}
                          >
                            N/A
                          </Typography>
                        </Box>
                      )}
                      {selectedExecution.metrics.memoryPeak !== undefined ? (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="caption">Memory Peak:</Typography>
                          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                            {formatMemory(selectedExecution.metrics.memoryPeak)}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "text.disabled" }}>
                            Memory Peak:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: "monospace", color: "text.disabled" }}
                          >
                            N/A
                          </Typography>
                        </Box>
                      )}
                      {selectedExecution.metrics.ioOperations !== undefined ? (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="caption">I/O Operations:</Typography>
                          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                            {selectedExecution.metrics.ioOperations.toLocaleString()}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "text.disabled" }}>
                            I/O Operations:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: "monospace", color: "text.disabled" }}
                          >
                            N/A
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* Data output */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <DataObject fontSize="small" />
                      Output Data
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "background.default",
                        p: 1.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        maxHeight: 200,
                        overflow: "auto",
                      }}
                    >
                      {selectedExecution.output ? (
                        <pre
                          style={{
                            fontSize: "11px",
                            fontFamily: "monospace",
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            color: theme.palette.text.primary,
                          }}
                        >
                          {typeof selectedExecution.output === "string"
                            ? selectedExecution.output
                            : JSON.stringify(selectedExecution.output, null, 2)}
                        </pre>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.disabled",
                            fontStyle: "italic",
                            display: "block",
                            textAlign: "center",
                            py: 2,
                          }}
                        >
                          No output data available
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* File Download Information for Excel Export Results */}
                {selectedExecution.fileResult && (
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "success.main",
                        bgcolor: "rgba(76, 175, 80, 0.1)",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "success.main",
                        }}
                      >
                        📄 Generated File
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="caption">Filename:</Typography>
                          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                            {selectedExecution.fileResult.filename}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="caption">Generated:</Typography>
                          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                            {formatTimestamp(selectedExecution.fileResult.generatedAt)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="caption">Status:</Typography>
                          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                            {selectedExecution.fileResult.message}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          console.log(
                            "📥 Downloading file from executions view:",
                            selectedExecution.fileResult.downloadUrl
                          );
                          window.open(selectedExecution.fileResult.downloadUrl, "_blank");
                        }}
                        sx={{
                          backgroundColor: "success.main",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "success.dark",
                          },
                          fontSize: "12px",
                        }}
                      >
                        📥 Download File
                      </Button>
                    </Paper>
                  </Grid>
                )}

                {/* Success message for completed executions */}
                {selectedExecution.status === "completed" && !selectedExecution.errorMessage && (
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: "success.main" + "10",
                        border: "1px solid",
                        borderColor: "success.main" + "30",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "success.main",
                        }}
                      >
                        <CheckCircle fontSize="small" />
                        Execution Status
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          color: "success.main",
                          bgcolor: "success.main" + "05",
                          p: 1,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "success.main" + "20",
                        }}
                      >
                        Execution completed successfully
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                {/* Error details if any */}
                {selectedExecution.errorMessage &&
                  (selectedExecution.status === "error" ||
                    selectedExecution.status === "failed") && (
                    <Grid item xs={12}>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: "error.main" + "10",
                          border: "1px solid",
                          borderColor: "error.main" + "30",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: "error.main",
                          }}
                        >
                          <BugReport fontSize="small" />
                          Error Details
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            color: "error.main",
                            bgcolor: "error.main" + "05",
                            p: 1,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "error.main" + "20",
                          }}
                        >
                          {selectedExecution.errorMessage}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                {/* Logs section */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Storage fontSize="small" />
                      Execution Logs
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "background.default",
                        p: 2,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        minHeight: 120,
                        fontFamily: "monospace",
                        fontSize: "11px",
                        color: "text.secondary",
                      }}
                    >
                      {selectedExecution.logs ? (
                        // Show real logs if available
                        selectedExecution.logs
                      ) : (
                        // Show basic execution timeline based on real data
                        <>
                          {selectedExecution.startTime &&
                            formatSafeDate(selectedExecution.startTime) && (
                              <>
                                {`[${formatSafeDate(selectedExecution.startTime)}] INFO: Execution started for ${selectedExecution.nodeName}`}
                                <br />
                              </>
                            )}
                          {selectedExecution.recordsProcessed && (
                            <>
                              {`[${formatSafeDate(selectedExecution.startTime) || new Date().toISOString()}] INFO: ${selectedExecution.recordsProcessed} records processed`}
                              <br />
                            </>
                          )}
                          {selectedExecution.errorMessage ? (
                            <span style={{ color: theme.palette.error.main }}>
                              {`[${formatSafeDate(selectedExecution.endTime || selectedExecution.startTime) || new Date().toISOString()}] ERROR: ${selectedExecution.errorMessage}`}
                            </span>
                          ) : selectedExecution.endTime &&
                            formatSafeDate(selectedExecution.endTime) ? (
                            `[${formatSafeDate(selectedExecution.endTime)}] INFO: Execution completed successfully`
                          ) : selectedExecution.status === "running" ? (
                            <span style={{ color: theme.palette.warning.main }}>
                              {`[${new Date().toISOString()}] INFO: Execution in progress...`}
                            </span>
                          ) : (
                            <span
                              style={{ color: theme.palette.text.disabled, fontStyle: "italic" }}
                            >
                              No detailed logs available for this execution
                            </span>
                          )}
                        </>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ExecutionsView;
